import { type FunctionComponent, useEffect, useState } from "react";
import { AnimatePresence, Button, type ButtonProps, type ColorTokens, Spinner, type TextProps, type ThemeName, View, XStack } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import { HapticsService, useDynamicBorderRadius } from "../utils";
export type LoadingButtonProps = ButtonProps & {
  color?: ColorTokens;
  textProps?: TextProps;
  theme?: ThemeName;
  loading?: boolean;
  gradientOff?: boolean;
  icon?: FunctionComponent<IconProps>;
  iconAfter?: FunctionComponent<IconProps>;
  stickyLoading?: boolean;
  disableHaptic?: boolean;
  customHaptic?: () => void;
  bold?: boolean;
  autoHeight?: boolean;
};

/**
 * This is our main button component. By default we use our primary gradient
 * use gradientOff to turn off the gradient and then apply any custom colors
 * or theming you want. To include an icon simply use the icon prop and pass.
 *
 * StickyLoading: sometimes the loading prop finishes put pushing a new screen is slow, when we know there cant be an error
 * we can use stickyLoading to keep the loading state for extra time. This should probably be improved in the future open to
 * ideas
 */
export const LoadingButton = (props: LoadingButtonProps) => {
  const {
    loading,
    stickyLoading,
    disableHaptic,
    customHaptic,
    disabled,
    icon: Icon,
    iconAfter: IconAfter,
    color = "$color",
    height = 50,
    bold = true,
    theme = "granted",
    size = "$4",
    textProps,
    autoHeight,
    backgroundColor,
    ...rest
  } = props;
  const [sticky, setSticky] = useState(loading);
  const haptic = customHaptic !== undefined ? customHaptic : HapticsService.selection;

  // Primary granted buttons need explicit light text; $color can resolve incorrectly in theme cascade
  const effectiveColor = disabled ? color === "$color" ? "$textSubtle" : color : theme === "granted" && color === "$color" ? "$textInverse" : color;
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (stickyLoading && sticky === true) {
      timeoutId = setTimeout(() => {
        setSticky(loading);
      }, 200);
    } else {
      setSticky(loading);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading, stickyLoading, sticky]);
  return <Button size={size} height={autoHeight ? undefined : height} paddingVertical={0} paddingHorizontal={size} borderWidth={0} disabled={disabled} borderRadius="$brFull" opacity={1} ai="center" jc="center" transition="200ms easeCubic" backgroundColor={backgroundColor ?? (theme === "granted" ? "$surfaceStrong" : undefined)} disabledStyle={{
    backgroundColor: "$bgDisabledSubtle"
  }} hoverStyle={{
    backgroundColor: theme === "granted" ? "$surfaceStrongHover" : "$color5"
  }} pressStyle={{
    backgroundColor: theme === "granted" ? "$surfaceStrongHover" : "$color4",
    scale: 0.95
  }} focusStyle={{
    outlineWidth: 0,
    outlineStyle: "none"
  }} onPressOut={() => {
    if (!disableHaptic) {
      void haptic();
    }
  }} theme={(theme === "granted" ? "light_granted_Button" : theme) as ThemeName} {...rest} data-sentry-element="Button" data-sentry-component="LoadingButton" data-sentry-source-file="LoadingButton.tsx">
      <XStack jc="center" ai="center" gap="$2" position="relative" data-sentry-element="XStack" data-sentry-source-file="LoadingButton.tsx">
        {Icon ? <View opacity={sticky ? 0 : 1} transition="quick" animateOnly={["opacity"]}>
            <Button.Icon scaleIcon={props.scaleIcon}>
              <Icon color={effectiveColor}></Icon>
            </Button.Icon>
          </View> : null}
        {props.children ? <Button.Text unstyled opacity={sticky ? 0 : 1} transition="quick" animateOnly={["opacity"]} color={effectiveColor}
      // eslint-disable-next-line react/forbid-component-props
      fontWeight={bold ? "500" : "400"} {...textProps}>
            {props.children}
          </Button.Text> : null}
        {IconAfter ? <View opacity={sticky ? 0 : 1} transition="quick" animateOnly={["opacity"]}>
            <Button.Icon scaleIcon={props.scaleIcon}>
              <IconAfter color={effectiveColor}></IconAfter>
            </Button.Icon>
          </View> : null}
        <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="LoadingButton.tsx">
          {sticky ? <View position="absolute" left={0} top={0} right={0} bottom={0} jc="center" ai="center" enterStyle={{
          opacity: 0
        }} exitStyle={{
          opacity: 0
        }} animateOnly={["opacity"]} transition="quick" zIndex={100}>
              <Spinner f={0} color={effectiveColor} key="loading-spinner" y={0} size="small" />
            </View> : null}
        </AnimatePresence>
      </XStack>
    </Button>;
};
export const TextButton = (props: LoadingButtonProps) => {
  return <LoadingButton bw={0} backgroundColor="$background0" pressStyle={{
    backgroundColor: "$cream8",
    scale: 0.95
  }} hoverStyle={{
    backgroundColor: "$backgroundTertiary"
  }} color="$textSubtle" w="100%" {...props} data-sentry-element="LoadingButton" data-sentry-component="TextButton" data-sentry-source-file="LoadingButton.tsx" />;
};
export const SecondaryButton = (props: LoadingButtonProps) => {
  return <LoadingButton bw={0} backgroundColor="$groupedSecondary" pressStyle={{
    backgroundColor: "$strokePrimary",
    scale: 0.95
  }} hoverStyle={{
    backgroundColor: "$strokePrimary"
  }} color="$textDefault" br="$10" {...props} data-sentry-element="LoadingButton" data-sentry-component="SecondaryButton" data-sentry-source-file="LoadingButton.tsx" />;
};
export const OutlinedButton = (props: LoadingButtonProps) => {
  return <LoadingButton bw={1} borderColor="$borderDefault" backgroundColor="$surface2" pressStyle={{
    backgroundColor: "transparent",
    scale: 0.95,
    borderColor: "$borderDefaultHover"
  }} color={props.disabled ? "$textSubtle" : "$textDefault"} hoverStyle={{
    bg: "$surface2Hover",
    borderColor: "$borderDefaultHover"
  }} disabledStyle={{
    backgroundColor: "$bgDisabledSubtle",
    borderColor: "$borderStrong"
  }} {...props} data-sentry-element="LoadingButton" data-sentry-component="OutlinedButton" data-sentry-source-file="LoadingButton.tsx" />;
};
export const WrappableOutlinedButton = (props: LoadingButtonProps) => {
  const {
    borderRadius,
    handleLayout
  } = useDynamicBorderRadius();
  return <View flex={1} onLayout={handleLayout} data-sentry-element="View" data-sentry-component="WrappableOutlinedButton" data-sentry-source-file="LoadingButton.tsx">
      <OutlinedButton textProps={{
      fontSize: "$4",
      flexWrap: "wrap",
      whiteSpace: "normal",
      display: "block",
      maxWidth: "100%",
      numberOfLines: undefined,
      color: props.disabled ? "$textSubtle" : "$textDefault",
      fontWeight: "400",
      ...props.textProps
    }} borderRadius={borderRadius} height="auto" p="$3" alignSelf="flex-end" alignItems="flex-start" flexDirection="column" flex={1} disabledStyle={{
      bg: "$bgDisabledSubtle",
      borderColor: "$borderStrong"
    }} cursor={props.disabled ? undefined : "pointer"} borderColor="$borderDefault" {...props} data-sentry-element="OutlinedButton" data-sentry-source-file="LoadingButton.tsx" />
    </View>;
};